/**
 * layout.js
 */

import React from 'react';
import { useTheme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import ReactGA4 from 'react-ga4';
import {
  useMediaQuery,
  Box,
  Button,
  Drawer,
  Hidden,
  Typography,
} from '@mui/material';
import clsx from 'clsx';
import { useLocation, Link } from 'react-router-dom';
import { matchPath } from "react-router";
import Smartlook from 'smartlook-client';
import * as Sentry from "@sentry/react";
import { TOUR_BEACON_VIEW_LIMIT, ONBOARDING_RECORDS_LIMIT, Types } from 'utils/constants';
import analytics from 'utils/analytics';
import { isIE } from "utils/app-context";
import logger from 'utils/logger';
import { UserContext } from 'contexts/user/user-context';
import { DogsContext } from 'contexts/dogs/dogs-context';
import { TourContext } from 'contexts/tour/tour-context';
import useRecordStore from 'contexts/record-store';
import { TRACKING_STATE } from 'components/tracking/tracking-common';
import { LiveTrackingContext } from 'components/tracking/live-tracking/live-tracking-context';
import TrackingDrawer from 'components/tracking/live-tracking/tracking-drawer';
import PrimaryRouter from 'routing/primary-router';
import Menu from 'components/layout/menu';
import Notifications from 'components/notifications';
import Icon from 'components/icon';
import Tour from 'components/tour';
import useTours from 'components/tour/tour-data';
import PTAppBar from './app-bar';
import Notices from './notices';


const useStyles = makeStyles(theme => ({
  fullHeight: {
    height: '100%',
  },
  root: {
    flex: 1,
    position: 'relative',
    margin: '0 auto',
    width: '100vw',
    overflowY: 'scroll',
  },
  page: {
    display: 'flex',
    margin: '0 auto',
    width: '100%',
    minHeight: '100vh',
  },
  drawer: {
    marginTop: 64,
    [theme.breakpoints.up('lg')]: {
      width: theme.size.menuDrawerWidth,
      flexShrink: 0,
    },
  },
  menuContainer: {
    backgroundColor: theme.palette.comp.mainMenu.background,
    color: theme.palette.sys.onPrimary,
    width: theme.size.menuDrawerWidth,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    '&.mobile-drawer': {
      backgroundColor: theme.palette.comp.mainMenu.backgroundMobile,
    }
  },
  imageContainer: {
    maxHeight: 700,
    width: '100%',
    backgroundColor: theme.palette.comp.mainMenu.background,
    backgroundImage: `url(${theme.palette.comp.mainMenu.image})`,
    backgroundBlendMode: 'multiply',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'top',
  },
  contentOuter: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.default,
    textAlign: 'left',
    minHeight: '100vh',
    maxWidth: '100%',
    paddingTop: theme.spacing(7),
    [theme.breakpoints.up('sm')]: {
      paddingTop: theme.spacing(8),
    },
  },
  nowrap: {
    whiteSpace: 'nowrap',
  },
  footer: {
    color: theme.palette.grey.colors[4],
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(3),
    [theme.breakpoints.up('lg')]: {
      marginTop: theme.spacing(5),
      marginBottom: theme.spacing(2),
    },
  },
  footerMargin: {
    margin: `${theme.spacing(1)} ${theme.spacing(3)}`,
  },
  footerLink: {
    color: theme.palette.primary.colors[4],
  }
}));


export default function Layout(props) {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  const { user } = React.useContext(UserContext);
  const { dogs, getActiveDogs } = React.useContext(DogsContext);
  const { activeTour, runTour, tourStep, beaconViews } = React.useContext(TourContext);
  const { trackingState, setIsTrackingOpen } = React.useContext(LiveTrackingContext);
  const { tourData, stopTour } = useTours();
  const location = useLocation();
  const recordStore = useRecordStore();
  const status = recordStore.useStatus();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handlerSubscription = user
    ?.subscription
    ?.subscriptions
    ?.find(s => s.role.id === Types.Role.HANDLER);
  const userRole = user?.role?.name ?? '';
  const planType = handlerSubscription?.type?.name || 'N/A';
  const activeDogCount = React.useMemo(() => getActiveDogs().length, [getActiveDogs]);
  const numRecs = status.stats.recordCount.get();
  const isRecsLoading = status.isLoading.get();
  const isAdminRoute = location.pathname.startsWith('/admin');


  // PT1-RETIRE
  const oldRoutes = [
    '^/custom-entries'
  ];
  const isPT1 = oldRoutes.find(r => window.location.pathname.match(r) !== null) !== undefined;

  
  // PT1-RETIRE
  // when we have a user, try to move the main content (old site)
  React.useEffect(() => {
    if (user && dogs && isPT1) {
      const source = document.querySelector('#content');
      const target = document.querySelector('.old_content');
      if (source && target) {
        [...source.children].forEach(element => {
          target.insertAdjacentElement('beforeEnd', element);
        });
      }
    }
  }, [user, dogs, isPT1]);


  // turn off any active tours when page navigation occurs
  // or if the beacon for the active tour has been seen more than TOUR_BEACON_VIEW_LIMIT number of times
  React.useEffect(() => {
    if (
      (activeTour >= 0 && tourData[activeTour] && !matchPath(location.pathname, tourData[activeTour].route))
      || (beaconViews[activeTour] > TOUR_BEACON_VIEW_LIMIT)
    ) {
      stopTour();
    }

    if (beaconViews[activeTour] > TOUR_BEACON_VIEW_LIMIT) {
      analytics.trackGA4('event', { name: 'tour_use', params: { interaction_type: 'tour_ignored', tour_name: tourData[activeTour].name } });
    }
  }, [location.pathname, activeTour, beaconViews, tourData, stopTour]);


  // initialize Intercom messaging service
  React.useEffect(() => {
    if (!user || !dogs) return;

    if (user.isImpersonated) {
      // If the user is impersonated, don't initialize Intercom
      window.Intercom?.('shutdown');
      return;
    }

    // Initialize Intercom normally. Note that changing the name of a key requires updating the associated
    // attribute in Intercom.
    window.intercomSettings = {
      app_id: "o89sg5k0",
      name: user.name,
      email: user.email,
      phone: user.phone,
      Department: user.departmentName,
      created_at: Math.floor(Date.parse(user.signupDate) / 1000),
      'PACKTRACK ID': user.id,
      'Dog count': dogs?.length,
      'Chargify Customer ID': user.subscription?.customerId || 'N/A',
      'Plan type': planType, 
      'Plan status': user.subscription?.handlerSubcription?.status?.name || 'N/A',
      'Last web login': Math.floor(Date.parse(user.lastWebLogin) / 1000),
      'Last app login': Math.floor(Date.parse(user.lastAppLogin) / 1000),
      'Current role': userRole,
      'Exercises': user.completeExercisesCount,
      'Deployments': user.deploymentCount,
    };

    // init sentry
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.name,
      role: user.role.name,
      dogCount: dogs.length,
      activeDogCount
    });
    
    return () => {
      // Shutdown Intercom when the component unmounts
      window.Intercom?.('shutdown');  
    }
  }, [user, dogs, activeDogCount, planType, userRole]);


  // show/hide intercom help button
  React.useEffect(() => {
    if (window.Intercom) {
      if (isMobile || isAdminRoute) {
        window.Intercom('update', {
          custom_launcher_selector:'#intercom-chat-link',
          hide_default_launcher: true,
        });
      } else {
        window.Intercom('update', {
          custom_launcher_selector:'#intercom-chat-link',
          hide_default_launcher: false,
        });
      }
    }
  }, [isMobile, isAdminRoute]);


  // Update user analytics properties if they've changed
  React.useEffect(() => {
    if (!isRecsLoading) {
      Smartlook.properties({
        plan_type: planType,
        role_type: userRole,
      });

      // Send a Smartlook analytics event if this is a new/onboarding user.
      // The global record counts are used as a (very) imperfect measure of engagement.
      if (numRecs < ONBOARDING_RECORDS_LIMIT) {
        if (numRecs >= 1) {
          Smartlook.track("onboard_1+_recs");
          logger.logAnalytics('[layout.js] Sending Smartlook event: onboard_1+_recs');
        }
        if (numRecs >= 5) {
          Smartlook.track("onboard_5+_recs");
          logger.logAnalytics('[layout.js] Sending Smartlook event: onboard_5+_recs');
        }
      }

      // Unfortunately, GA4 still doesn't support user level metrics so we have to translate
      // the counts to discrete dimension values
      ReactGA4.set({
        'user_properties': {
          'user_role': userRole,
          'user_plan_type': planType,
          'user_dog_count': activeDogCount,
          'user_num_recs': numRecs <= 0 ? "0"
            : numRecs <= 1 ? "1"
            : numRecs <= 2 ? "2"
            : numRecs <= 5 ? "3-5"
            : numRecs <= 10 ? "6-10"
            : numRecs <= 20 ? "11-20"
            : numRecs <= 50 ? "21-50"
            : numRecs <= 100 ? "51-100"
            : "100+",
        }
      });

      logger.logAnalytics('[layout.js] Analytics properties changed: ', userRole, planType, activeDogCount, numRecs);
    }
  }, [isRecsLoading, numRecs, planType, userRole, activeDogCount]);


  // mobile drawer toggle
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  }


  return <>
    {user && dogs &&
    <Box sx={{ height: '100vh', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div className={`${classes.root}`} id='main-scroll'>
        <PTAppBar drawerToggle={handleDrawerToggle} />

        <div className={classes.page}>

          {/* sidebar menu is hidden for admin routes */}
          {!isAdminRoute &&
          <nav className={classes.drawer} aria-label="Main menu">
            {/* mobile menu */}
            <Hidden lgUp implementation="css">
              <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                classes={{
                  paper: `${classes.menuContainer} mobile-drawer`,
                }}
                ModalProps={{
                  keepMounted: true,
                }}
              >
                <Menu onSelect={(item) => setMobileOpen(false)} />
              </Drawer>
            </Hidden>
            {/* desktop menu */}
            <Hidden lgDown implementation="css" className={classes.fullHeight}>
              <Box className={classes.menuContainer} height="100%">
                <Box flex={1}>
                  <Menu />
                </Box>
                {!isIE() &&
                <Box flex={1} className={classes.imageContainer} />}
              </Box>
            </Hidden>
          </nav>}

          <div
            className={classes.contentOuter}
            data-testid='page-content'

            // PT1-RETIRE custom bg color
            style={{backgroundColor: isPT1 ? '#fff' : theme.palette.background.default} }
          >
            <Box sx={{ 
              flexGrow: 1,
              width: isAdminRoute ? '100%' : theme.size.contentMax,
              maxWidth: '100%',
              margin: '0 auto',
              display: 'flex',
              flexDirection: 'column',
            }}>
              <PrimaryRouter />

              {/* PT1-RETIRE */}
              {isPT1 &&
              <div 
                className="old_content" 
                style={{
                  display: 'flex', 
                  flexDirection: 'column', 
                  flex: 1, 
                  flexBasis: 'auto',
                }}>
              </div>}
            </Box>

            {/* footer */}
            {!isMobile && !isAdminRoute &&
            <Box className={classes.footer} sx={{fontSize: 'sizes.1'}} align="center">
              <Typography variant="body2" className={classes.footerMargin} sx={{fontSize: 'sizes.1'}}>
                <span className={classes.nowrap}>{'Copyright © '}{new Date().getFullYear() + ' '}</span>
                <span> </span>
                <span className={classes.nowrap}>Canine Development Group Inc.</span>
                <span> </span>
                <span className={classes.nowrap}>All rights reserved worldwide.</span>
                <span> </span>
              </Typography>
              <Link color="inherit" to="/terms-of-use" className={clsx(classes.footerMargin, classes.nowrap, classes.footerLink)}>Terms of Use</Link>
              <Link color="inherit" to="/privacy-policy" className={clsx(classes.footerMargin, classes.nowrap, classes.footerLink)}>Privacy Policy</Link>
            </Box>}
          </div>

        </div>

        <Notifications />
        <Notices />
        {(activeTour >= 0) &&
        <Tour
          run={runTour}
          stepIndex={tourStep}
          handleTourCallback={tourData[activeTour].callback}
          steps={tourData[activeTour].steps}
        />}

      </div>

      <TrackingDrawer />

      {/* show active tracking overlay if state is anything but UNSTARTED */}
      {trackingState !== TRACKING_STATE.UNSTARTED &&
      <Box sx={{ 
        height: 64,
        p: 1,
        backgroundColor: 'sys.surfaceContainerHighest',
        display: 'flex'
      }}>
        <Button
          onClick={() => setIsTrackingOpen(true)}
          fullWidth
          color='secondary'
          variant='contained'
          startIcon={<Icon type='location-on' />}
          sx={{
            backgroundColor: 'sys.secondaryContainer',
            textTransform: 'none',
            justifyContent: 'flex-start',
            fontWeight: '400',
            boxShadow: theme => theme.shadows[1],
            '& .MuiButton-startIcon .MuiSvgIcon-root': {
              fontSize: 'sizes.6',
              color: 'sys.onSecondaryVariant'
            }
          }}
        >
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
            <Box sx={{ whiteSpace: 'nowrap' }}>Tracking is active.</Box>
            <Typography variant='subtitle2' sx={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'inherit' }}>
              RETURN TO TRACK 
              <Icon type='nav-forward' sx={{ fontSize: 'sizes.2', ml: 1 }} />
            </Typography>
          </Box>
        </Button>
      </Box>}

    </Box>}
  </>;
}